import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { MatBadgeModule } from '@angular/material/badge';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { Subject, filter, switchMap, take, takeUntil } from 'rxjs';
import { CompanyService } from 'app/services/company.service';
import { AccessApprovalService } from 'app/services/access-approval.service';
import { AccessApprovalRequestModel } from 'app/models/access-approval-request.model';
import { AutoTranslateDirective } from 'app/shared/directives/auto-translate.directive';
import { AutoTranslatePipe } from 'app/shared/pipes/translate.pipe';

/**
 * Sininho do médico remoto: pedidos de entrada sem turno aguardando. Toast
 * imediato quando chega um novo pedido + contador no ícone + overlay pequeno
 * (mat-menu) com a lista — sem página dedicada. Só é montado quando
 * `user.is_regulator` (o mesmo flag de `/admin/access/regulators`) — ver
 * `*ngIf="user?.is_regulator"` no layout. Não é permissão: quem pode ver/agir
 * é decidido pelo backend em cima desse mesmo flag + vínculo com a empresa.
 *
 * Não existe ação de "aprovar" aqui: a única ação além de rejeitar é
 * "Abrir alocação", que leva para a tela real de alocação já com essa pessoa
 * pré-selecionada (via query params lidos por `ShiftListComponent`) — a
 * entrada só libera de verdade quando o médico aloca a pessoa lá.
 */
@Component({
    selector: 'access-approval-bell',
    templateUrl: './access-approval-bell.component.html',
    styleUrls: ['./access-approval-bell.component.scss'],
    encapsulation: ViewEncapsulation.None,
    standalone: true,
    imports: [
        CommonModule,
        MatBadgeModule,
        MatButtonModule,
        MatIconModule,
        MatMenuModule,
        MatTooltipModule,
        AutoTranslateDirective,
        AutoTranslatePipe,
    ],
})
export class AccessApprovalBellComponent implements OnInit, OnDestroy {
    requests: AccessApprovalRequestModel[] = [];
    rejectingId: number | null = null;

    private companyId: number | null = null;
    private readonly _unsubscribeAll = new Subject<void>();

    constructor(
        private readonly companyService: CompanyService,
        private readonly accessApprovalService: AccessApprovalService,
        private readonly toastr: ToastrService,
        private readonly router: Router,
    ) {}

    ngOnInit(): void {
        this.companyService.currentCompany$
            .pipe(
                takeUntil(this._unsubscribeAll),
                filter((company) => !!company?.company_id),
            )
            .subscribe((company) => {
                if (this.companyId === company.company_id) return;
                this.companyId = company.company_id;
                this.loadPending();
            });

        this.accessApprovalService
            .onCreated()
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((event) => {
                this.toastr.warning(event.user_name || 'Usuário', 'Pedido de entrada sem turno', { timeOut: 8000 });
                if (event.company_id !== this.companyId) return;
                if (this.requests.some((r) => r.access_approval_request_id === event.request_id)) return;
                this.requests = [
                    ...this.requests,
                    new AccessApprovalRequestModel({
                        access_approval_request_id: event.request_id,
                        company_id: event.company_id,
                        user_id: event.user_id,
                        user_name: event.user_name,
                        user_profile_type: event.user_profile_type,
                        status: 'PENDING',
                        createdAt: new Date(event.requested_at),
                    }),
                ];
            });

        this.accessApprovalService
            .onDecided()
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((event) => {
                this.requests = this.requests.filter((r) => r.access_approval_request_id !== event.request_id);
            });
    }

    ngOnDestroy(): void {
        this._unsubscribeAll.next();
        this._unsubscribeAll.complete();
    }

    /**
     * Leva para a tela real de alocação já com essa pessoa pré-selecionada.
     * `ShiftListComponent` lê esses query params depois de carregar os
     * colaboradores operacionais da empresa e abre o assistente sozinho.
     */
    openAllocationFor(request: AccessApprovalRequestModel): void {
        this.router.navigate(['/shifts/allocations'], {
            queryParams: {
                open_user_id: request.user_id,
                open_user_name: request.user_name,
                open_role: request.user_profile_type || 'OTHER',
            },
        });
    }

    reject(request: AccessApprovalRequestModel): void {
        this.rejectingId = request.access_approval_request_id;
        this.accessApprovalService.reject(request.access_approval_request_id).subscribe({
            next: () => {
                this.requests = this.requests.filter((r) => r.access_approval_request_id !== request.access_approval_request_id);
                this.rejectingId = null;
            },
            error: () => (this.rejectingId = null),
        });
    }

    private loadPending(): void {
        this.companyService.currentCompany$
            .pipe(
                take(1),
                switchMap(() => this.accessApprovalService.listPending(this.companyId)),
            )
            .subscribe({
                next: (requests) => (this.requests = requests),
                error: () => (this.requests = []),
            });
    }
}
